// ─── 🎁 Il premio a chi organizza, in crediti ────────────────────────────────
//
// Voce 33, mockup approvato il 26/09/2026 (strade 1A · 2A · 3A).
//
// 🗣️ *«Ogni volta che una persona nell'arco di sette giorni … organizza una
// partita gli vengono accreditati»* · *«due campi lo stesso giorno si contano»*
// · *«solo dopo ogni partita … parzialmente accreditate, ma non ancora
// disponibili per pagare»* · *«devo fare una cosa di crediti»*.
//
// ⚖️ **Crediti, non denaro**: 1 credito = 1,00 € di sconto sulla quota di una
// partita. Non si prelevano, non si rimborsano, non si passano ad altri
// (DPR 430/2001, art. 4 e art. 6 lett. c).
import { z } from 'zod';
import { dataSchema } from './circolo.js';
import { giorniFra } from './griglia.js';
/** Quanto sconto vale un credito, in centesimi. Fisso: «1 credito = 1,00 €». */
export const VALORE_CREDITO_CENTS = 100;
/** La finestra: un'altra partita sua entro 7 giorni di calendario, 7 compreso, anche lo stesso giorno. */
export const GIORNI_DEL_PREMIO = 7;
// ── Le impostazioni ─────────────────────────────────────────────────────────
export const impostazioniPremioSchema = z.object({
    /** Spento di partenza: si accende dopo il commercialista. */
    attivo: z.boolean(),
    /** Da che giorno contano le partite: niente premi per le partite di prima. */
    dal: dataSchema.nullable(),
    creditiPerPartita: z.number().int().min(1).max(20),
    // 🔄 Voce 36 (27/09/2026): quanto valgono i Crediti e quando si avvisa non
    // stanno più qui ⇒ `crediti.ts`.
});
export const IMPOSTAZIONI_PREMIO_DI_PARTENZA = {
    attivo: false,
    dal: null,
    creditiPerPartita: 2,
};
// ── La regola dei 7 giorni ──────────────────────────────────────────────────
/**
 * ⭐ Una partita ha una compagna se chi l'ha organizzata ne ha un'ALTRA, ancora
 * in piedi, entro 7 giorni di calendario (7 compreso), prima o dopo — anche la
 * stessa sera: due campi per un torneo si fanno premio a vicenda.
 */
export function haCompagna(partita, altre) {
    return altre.some((q) => q.id !== partita.id && Math.abs(giorniFra(partita.date, q.date)) <= GIORNI_DEL_PREMIO);
}
/**
 * ⭐⭐ **Il credito che scade si consuma per primo, a cominciare da quello che
 * scade prima** (§ 2.3, FIFO sulle scadenze).
 *
 * ⚖️ Si RICALCOLA dalla storia, non si tiene scritto da nessuna parte: il saldo
 * resta la somma nuda di sempre (`member_wallet_balance`), e questa funzione
 * dice soltanto **di che denaro** è fatto. Una seconda colonna «credito
 * residuo» sarebbe un secondo numero sullo stesso denaro, e un giorno i due
 * direbbero cose diverse.
 *
 * Le regole, in ordine di tempo:
 *   · un'entrata con la scadenza apre un lotto; senza, è denaro libero;
 *   · la scadenza di un lotto (`lotTransactionId`) lo chiude;
 *   · lo storno di un'uscita restituisce **esattamente** ciò che quell'uscita
 *     aveva preso, lotti compresi: annullare un pagamento non allunga la vita al
 *     credito, e non gliela accorcia;
 *   · ogni altra uscita prende dai lotti ancora vivi in quell'istante, prima
 *     quello che scade prima, poi dal denaro libero.
 */
export function lottiDelBorsellino(movimenti) {
    const ordinati = [...movimenti].sort((a, b) => Date.parse(a.recordedAt) - Date.parse(b.recordedAt) || a.id.localeCompare(b.id));
    const lotti = new Map();
    let libero = 0;
    /** Cosa ha preso ogni uscita, per poterlo restituire allo storno. */
    const prese = new Map();
    const prendi = (id, quanto, istante) => {
        const vivi = [...lotti.values()]
            .filter((l) => l.remainingCents > 0 && Date.parse(l.expiresAt) > istante)
            .sort((a, b) => Date.parse(a.expiresAt) - Date.parse(b.expiresAt) || a.id.localeCompare(b.id));
        const presa = { lotti: [], libero: 0 };
        let resta = quanto;
        for (const l of vivi) {
            if (resta === 0)
                break;
            const x = Math.min(resta, l.remainingCents);
            l.remainingCents -= x;
            resta -= x;
            presa.lotti.push([l.id, x]);
        }
        libero -= resta;
        presa.libero = resta;
        prese.set(id, presa);
    };
    for (const m of ordinati) {
        const istante = Date.parse(m.recordedAt);
        if (m.direction === 'charge') {
            if (m.lotTransactionId) {
                const l = lotti.get(m.lotTransactionId);
                if (l) {
                    const x = Math.min(m.amountCents, l.remainingCents);
                    l.remainingCents -= x;
                    libero -= m.amountCents - x;
                }
                else
                    libero -= m.amountCents;
                continue;
            }
            if (m.reversesTransactionId && lotti.has(m.reversesTransactionId)) {
                // Lo storno di un credito che scade: si toglie da quel lotto.
                const l = lotti.get(m.reversesTransactionId);
                const x = Math.min(m.amountCents, l.remainingCents);
                l.remainingCents -= x;
                libero -= m.amountCents - x;
                continue;
            }
            prendi(m.id, m.amountCents, istante);
            continue;
        }
        // Entrate: ricarica o rimborso.
        const storna = m.reversesTransactionId ? prese.get(m.reversesTransactionId) : undefined;
        if (storna) {
            for (const [id, x] of storna.lotti) {
                const l = lotti.get(id);
                if (l)
                    l.remainingCents += x;
            }
            libero += m.amountCents - storna.lotti.reduce((s, [, x]) => s + x, 0);
            continue;
        }
        if (m.expiresAt) {
            lotti.set(m.id, {
                id: m.id,
                amountCents: m.amountCents,
                remainingCents: m.amountCents,
                expiresAt: m.expiresAt,
            });
        }
        else
            libero += m.amountCents;
    }
    return {
        lotti: [...lotti.values()].sort((a, b) => Date.parse(a.expiresAt) - Date.parse(b.expiresAt)),
        liberoCents: libero,
    };
}
// ── I movimenti, e i crediti vivi ───────────────────────────────────────────
export const TIPI_MOVIMENTO_PREMIO = [
    'premio',
    'ripristino',
    'storno_uso',
    'no_show',
    'uso',
    'scadenza',
];
export const ENTRANO = new Set(['premio', 'ripristino', 'storno_uso']);
export const ETICHETTE_MOVIMENTO_PREMIO = {
    premio: 'Premio organizzatore',
    ripristino: 'Premio rimesso (no-show annullato)',
    storno_uso: 'Tornati da uno storno',
    no_show: 'Tolti per no-show',
    uso: 'Usati per pagare',
    scadenza: 'Scaduti',
};
/**
 * ⭐ I crediti si contano a lotti con scadenza,
 * consumati prima quelli che scadono prima (⇒ `lottiDelBorsellino`). Il saldo
 * non si tiene scritto da nessuna parte: si ricalcola dalla storia.
 */
export function lottiDeiCrediti(movimenti) {
    return lottiDelBorsellino(movimenti.map((m) => ({
        id: m.id,
        direction: ENTRANO.has(m.kind) ? (m.kind === 'storno_uso' ? 'refund' : 'recharge') : 'charge',
        amountCents: m.credits,
        recordedAt: m.recordedAt,
        expiresAt: m.expiresAt,
        lotTransactionId: m.kind === 'scadenza' ? m.lotId : null,
        reversesTransactionId: m.kind === 'storno_uso' ? m.reversesId : null,
    })));
}
/** I crediti spendibili adesso: i lotti vivi, senza quelli scaduti che aspettano la loro riga. */
export function creditiDisponibili(l, adesso) {
    let n = Math.max(0, l.liberoCents);
    for (const x of l.lotti) {
        if (x.remainingCents > 0 && Date.parse(x.expiresAt) > adesso.getTime())
            n += x.remainingCents;
    }
    return n;
}
// ── Cosa vedono la Segreteria e il bot ──────────────────────────────────────
export const partitaInArrivoSchema = z.object({
    bookingId: z.uuid(),
    date: dataSchema,
    time: z.string(),
    courtName: z.string(),
    credits: z.number().int(),
});
export const crediteInScadenzaSchema = z.object({
    credits: z.number().int(),
    expiresOn: dataSchema,
});
export const movimentoPremioSchema = z.object({
    id: z.uuid(),
    kind: z.enum(TIPI_MOVIMENTO_PREMIO),
    /** Col segno: + entrano, − escono. */
    credits: z.number().int(),
    recordedAt: z.string(),
    /** La partita, detta per esteso («mer 7/10 · 20:00 · Campo 2»), quando c'è. */
    booking: z.string().nullable(),
});
export const premioDelSocioSchema = z.object({
    active: z.boolean(),
    creditsPerMatch: z.number().int(),
    creditValueCents: z.number().int(),
    months: z.number().int(),
    available: z.number().int(),
    incoming: z.array(partitaInArrivoSchema),
    expiring: z.array(crediteInScadenzaSchema),
    movements: z.array(movimentoPremioSchema),
});
/** I crediti di chi è in campo, per il mezzo «🎁 Crediti premio» della scheda. */
export const creditiDelGiocatoreSchema = z.object({
    memberId: z.uuid(),
    credits: z.number().int(),
});
//# sourceMappingURL=premio.js.map